import { ColorTabs } from '@src/components/common/ColorPicker';
import {
  getUnifiedValue,
  resolveElementFromPath,
} from '@src/utility/compositionSettings';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { ImagePreset } from '../../ImagePreset';
import { ColorControl } from './ColorControl';
import { ControlFieldLabel } from './ControlField';
import { NumberControl } from './NumberControl';

import type {
  ColorControlConfiguration,
  ControlRenderProps,
  NumberControlConfiguration,
  VideoControlConfiguration,
} from '../types';
import type { ElementState } from '@src/renderer/ElementState';
import type { ElementLike } from '@src/utility/compositionSettings';

/**
 * VideoControl
 * Renders a lightweight preview (VideoPreset) and delegates selection to the existing global video replacement modal.
 * On successful selection (modal closes with a new url), we call modifyProperty for each elementPath with the chosen url.
 * We listen to changes in the active element's source value to detect replacement rather than rewiring modal internals.
 */
export const VideoControl = observer(
  ({
    control,
    activeElement,
    modifyProperty,
    compositionMeta,
  }: ControlRenderProps<VideoControlConfiguration>) => {
    const { unifiedValue } = getUnifiedValue(
      control,
      activeElement,
      compositionMeta,
    );
    const initialUrl =
      typeof unifiedValue === 'string' ? unifiedValue : undefined;
    const videoElement = resolveElementFromPath(
      activeElement as unknown as ElementLike,
      control.elementPaths[0] || [],
    );

    const handleVideoSourceChange = (newUrl: string) => {
      modifyProperty(control, newUrl);
    };

    const chromaKeyColorControl: ColorControlConfiguration = {
      type: 'color',
      elementPaths: control.elementPaths,
      property: 'chroma_key_color',
      label: 'Chroma Key Color',
    };

    const chromaKeySensitivityControl: NumberControlConfiguration = {
      type: 'number',
      elementPaths: control.elementPaths,
      property: 'chroma_key_sensitivity',
      label: 'Chroma Key Sensitivity',
      unit: '%',
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <ControlFieldLabel>{control.label}</ControlFieldLabel>
        <ImagePreset
          activeElement={videoElement as unknown as ElementState}
          parentCompositionElement={activeElement}
          url={initialUrl || ''}
          onSourceChange={handleVideoSourceChange}
          isVideoControl={true}
        />
        <ChromaControlsContainer>
          <ChromaColorControl>
            <ColorControl
              control={chromaKeyColorControl}
              activeElement={activeElement}
              modifyProperty={modifyProperty}
              pickerTab={ColorTabs.custom}
              compositionMeta={compositionMeta}
            />
          </ChromaColorControl>
          <ChromaSensitivityControl>
            <NumberControl
              control={chromaKeySensitivityControl}
              activeElement={activeElement}
              modifyProperty={modifyProperty}
              min={1}
              max={100}
              compositionMeta={compositionMeta}
            />
          </ChromaSensitivityControl>
        </ChromaControlsContainer>
      </div>
    );
  },
);

export default VideoControl;

const ChromaControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const ChromaColorControl = styled.div`
  flex: 1;
`;

const ChromaSensitivityControl = styled.div`
  flex: 1;
`;
